import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Pomoc from '../components/Pomoc';
import SignaturePad, { OcenaIzPotpisa } from './SignaturePad';
import TourSketch from './TourSketch';
import FotografijeStanice from './FotografijeStanice';
import { FotoZapis, posalji as posaljiFotografije, telefonskoSkladiste } from './fotografije';
import {
  DriverEvent,
  DriverStop,
  DriverTour,
  EventType,
  OdbijenZapis,
  deviceNow,
  podeliPoTuri,
  posaljiGrupu,
  storage,
  zaServer
} from './outbox';

import { API_BASE as BASE } from '../services/apiBase';
import { trebaPovuciTuru } from './izmene';
import { osveziKupce } from './kupci';
import { ostatakTure, trebaPitatiZaOstatak } from './ostatak';
import { odstupanjeMinuta, porukaOSatu } from './sat';
import { izaberiTuru, jeDanasnja } from './izbor';
import { porukaBezVeze } from './tudjaTura';

/**
 * Razlozi izuzetka. Jedan tap, bez kucanja — vozač ovo radi na kiši.
 *
 * „Aparat oštećen" je dodat 28.09.2026. Do tada se nudio SAMO na preuzimanju, pa
 * je vozač kod kupca birao nešto približno — obično „Kupac odbio robu". To posle
 * izgleda kao problem sa kupcem, a nije: aparat je stigao oštećen i vraća se.
 * Razlika je bitna jer se po tim razlozima gleda šta se na terenu dešava.
 */
const EXCEPTIONS = [
  'Kupac nije kod kuće', 'Kupac odbio robu', 'Aparat oštećen', 'Adresa netačna', 'Nema pristupa'
];

/**
 * Razlozi kad roba treba da se uzme, a ne može. Drugi su od isporuke: ovde
 * kupca nema, nego prodavnice — i ako aparat nije tu, cela isporuka pada.
 */
const PICKUP_EXCEPTIONS = ['Aparata nema u prodavnici', 'Zatvoreno', 'Aparat oštećen'];

/**
 * Zašto se odlazi bez potpisa.
 *
 * Potpis je interni dokaz o isporuci. Kad ga nema, razlog je podatak — iz njega
 * se vidi da li se radi o kupcu koji neće, o telefonu koji neće, ili o robi
 * ostavljenoj bez kupca. Bez ovoga bi svih 39% neporpisanih izgledalo isto.
 */
const BEZ_POTPISA = [
  'Kupac ne može da potpiše',
  'Kupac odbio da potpiše',
  'Ostavljeno bez kupca',
  'Telefon neće da uzme potpis'
];

/** Razlozi promene redosleda. Ovo razdvaja šum od stvarnog znanja o terenu. */
const REORDER_REASONS = ['Radovi na putu', 'Kupac nedostupan', 'Kraći put', 'Drugo'];

/**
 * Roba koja se vraća u magacin, bez krivca.
 *
 * Postoji jer nijedan od ostalih razloga ne pokriva najobičniji slučaj: nije
 * stalo u vozilo, ili je isporuka namerno pošla nepotpuna. Bez ovog izbora bi
 * vozač birao „Kupac odbio robu", što nije tačno i posle se čita kao problem sa
 * kupcem.
 */
const OSTAJE_U_VOZILU = 'Ostaje u vozilu — vraćam u magacin';

/** Odgovor pri zatvaranju: bio je tamo i robu ostavio, samo nije štiklirao. */
const PREDATO_NA_KRAJU = 'Predato — potvrđeno pri zatvaranju ture';

/**
 * Stanica do koje vozač nije ni došao.
 *
 * Postoji jer nijedan izuzetak to ne pokriva: „Kupac nije kod kuće" tvrdi da je
 * bio tamo. Bez ovog izbora bi vozač birao nešto što nije tačno, a operater bi
 * sutradan zvao kupca koga niko nije ni tražio.
 */
const NISAM_STIGAO = 'Nisam stigao do ove adrese';

interface Props {
  /** Ključ iz linka. Vozač se ne prijavljuje — telefon radi bez mreže. */
  accessKey: string;
}

/**
 * Vozačev ekran.
 *
 * Radi bez mreže od trenutka kad je tura učitana. Svaki potez ide prvo u lokalni
 * red pa tek onda, ako signala ima, na server — nikad obrnuto. Zbog toga nijedno
 * dugme ne čeka odgovor i nijedan zapis se ne gubi kad veza padne usred posla.
 */
/** Navigacija do stanice. Isti oblik i iz spiska i iz objašnjenja. */
function mapsLink(stop: DriverStop): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${stop.latitude},${stop.longitude}`;
}

export default function DriverApp({ accessKey }: Props) {
  // Kes se prihvata samo ako je tura učitana za OVOG vozača. Ranije se čitao
  // slepo, pa je svako ko otvori link video poslednju turu koja je na tom
  // uređaju otvorena — na službenom telefonu koji menja ruke, tuđu.
  const [tour, setTour] = useState<DriverTour | null>(
    () => (storage.tourDriverKey() === accessKey ? storage.loadTour() : null)
  );
  const [pending, setPending] = useState<DriverEvent[]>(() => storage.outbox());
  const [odbijeno, setOdbijeno] = useState<OdbijenZapis[]>(() => storage.odbijeno());
  const [online, setOnline] = useState(navigator.onLine);

  /** Fotografije koje još nisu otišle. Stoje u IndexedDB-u, ne u redu zapisa. */
  const [fotoRed, setFotoRed] = useState<FotoZapis[]>([]);
  const ucitajFotoRed = useCallback(async () => {
    try {
      setFotoRed(await telefonskoSkladiste.sve());
    } catch {
      setFotoRed([]);
    }
  }, []);
  useEffect(() => { void ucitajFotoRed(); }, [ucitajFotoRed]);
  /**
   * Otvorena stanica se pamti u telefonu.
   *
   * Vozač sa ovog ekrana odlazi u Mape, i telefon tada aplikaciju često ugasi u
   * pozadini. Kad se vrati, mora da nastavi tamo gde je stao — a ne da traži
   * svoju stanicu među četrnaest.
   */
  const [openStopId, setOpenStopId] = useState<number | null>(
    () => storage.openStop());

  useEffect(() => {
    storage.rememberOpenStop(openStopId);
  }, [openStopId]);

  /**
   * Pri otvaranju ture otvara se stanica na kojoj se stoji.
   *
   * Vozač ekran otvara dok vozi ili dok nosi mašinu — ne da bira iz spiska nego
   * da vidi gde je i šta nosi. Gleda se samo pri promeni ture: zapamćena
   * stanica se poštuje, osim ako je u međuvremenu završena ili skinuta (vozač
   * je zatvorio aplikaciju posle isporuke, pa se vraća na gotovu stanicu).
   *
   * Unutar ture ovo ne dira ništa — stanicu koju je sam zatvorio ne otvaramo mu
   * ponovo pod prstom; sledeću otvara `depart`.
   */
  const tourId = tour?.id ?? null;

  useEffect(() => {
    const saved = storage.loadTour();
    if (!saved) return;

    const ziveStanice = [...saved.stops]
      .filter((s) => !s.removedAt && !s.actualDeparture)
      .sort((a, b) => a.sequence - b.sequence);

    setOpenStopId((trenutna) => {
      const josStoji = ziveStanice.some((s) => s.id === trenutna);
      if (trenutna !== null && josStoji) return trenutna;

      return ziveStanice.length > 0 ? ziveStanice[0].id : null;
    });
  }, [tourId]);

  /**
   * Da li je vozaču već objašnjeno kako se vraća sa Mapa.
   *
   * Navigacija otvara drugu aplikaciju preko cele slike i ovu sklanja. Ko to
   * nije video ranije, ne zna da je tura i dalje tu — pa se objašnjava jednom,
   * pre prvog odlaska, i pamti da je viđeno.
   */
  const [navigatingTo, setNavigatingTo] = useState<DriverStop | null>(null);
  const [signingStopId, setSigningStopId] = useState<number | null>(null);

  /** Stanica na kojoj je nešto ostalo, a vozač kreće dalje. Pita se zašto. */
  const [pitanjeOOstatku, setPitanjeOOstatku] = useState<number | null>(null);

  /** Stanica za koju se pita potpis pre odlaska. Vidi `traziPotpis`. */
  const [pitanjeOPotpisu, setPitanjeOPotpisu] = useState<number | null>(null);

  /** Vozac pise sta je sa vozilom. Prazno dok ne pritisne dugme za kvar. */
  const [kvarOtvoren, setKvarOtvoren] = useState(false);
  const [kvarTekst, setKvarTekst] = useState('');

  /** Vozač je pritisnuo „Završi turu" i prolazi kroz ono što nije odradio. */
  const [zatvaramTuru, setZatvaramTuru] = useState(false);

  /** Stanica koju vozač preteže napred. Bira razlog, ne kuca ga. */
  const [pitanjeORedosledu, setPitanjeORedosledu] = useState<number | null>(null);

  /** Stanica kojoj se piše beleška, i tekst dok se piše. */
  const [beleskaZa, setBeleskaZa] = useState<number | null>(null);
  const [beleska, setBeleska] = useState('');

  /** Pregled dana na skici. Vozač je otvori pre polaska, radi i bez signala. */
  const [showSketch, setShowSketch] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [potpisGreska, setPotpisGreska] = useState<string | null>(null);
  // Sat telefona naspram servera (analiza 2.0, D11). Meri se pri svakoj proveri verzije.
  const [satPoruka, setSatPoruka] = useState<string | null>(null);

  /**
   * Ture između kojih vozač bira sam.
   *
   * Popunjava se samo kad danas ima više od jedne nezavršene, a link ne kaže
   * koja je. Do 08.09.2026. se u tom slučaju otvarala prva i govorilo mu se da
   * za drugu otvori link koji operater nema nigde na ekranu.
   */
  const [izbor, setIzbor] = useState<{ id: number; name?: string; status?: string }[]>([]);
  const [busy, setBusy] = useState(false);

  /** Tura iz linka, kad je poruka nosi. Bez nje se uzima prva današnja. */
  const tourFromLink = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get('tura');
    const parsed = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(parsed) ? parsed : -1;
  }, []);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  /**
   * Povlači izmenjenu turu sa servera.
   *
   * Radi SAMO kad je red prazan. Ekran prikazuje poteze čim ih vozač napravi,
   * pre nego što odu na server; da se tura preuzme dok red nije ispražnjen,
   * ti potezi bi nestali sa ekrana iako nisu izgubljeni. Praznog reda nema šta
   * da se izgubi — server tada zna sve što i telefon.
   */
  const pullChanges = useCallback(async (tourId: number): Promise<void> => {
    // Gleda se samo red OVE ture: tuđi neposlat zapis (telefon koji menja
    // ruke) ne sme da zaustavi izmene koje operater šalje ovom vozaču.
    if (storage.outboxFor(tourId).length > 0) return;

    try {
      const response = await fetch(
        `${BASE}/api/driver/tours/${tourId}?kljuc=${encodeURIComponent(accessKey)}`);

      if (!response.ok) return;

      const fresh: DriverTour = await response.json();
      const previous = storage.loadTour();

      // EKRAN IDE PRVI, keš posle. Redosled je bio obrnut, a `saveTour` ume da
      // padne i bez mreže — na punom telefonu. Tada se `setTour` nije ni izvršio,
      // pa vozač NIJE videofmm ni izmenu ture koju je operater upravo dodao. To je
      // ista ona nevidljiva tačka zbog koje se skinuta stanica precrtava umesto da
      // se briše, samo kroz druga vrata.
      //
      // Keš je pogodnost — vozač po njemu nastavlja posle ponovnog otvaranja.
      // Ekran je ono po čemu radi SADA, pa se ne sme vezati za uspeh upisa.
      setTour(fresh);

      // Operater na svom ekranu vidi „vozač još nije preuzeo" dok ovo ne
      // stigne. Ako javljanje padne, sledeće preuzimanje ga ponavlja — bolje
      // dvaput nego da operater misli da vozač ne zna.
      void fetch(
        `${BASE}/api/driver/tours/${tourId}/preuzeto?kljuc=${encodeURIComponent(accessKey)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ version: fresh.version ?? 1 })
        }
      ).catch(() => { /* javice se sledeci put */ });

      const dodate = fresh.stops.filter(
        (s) => s.addedAt && !previous?.stops.some((old) => old.id === s.id));

      if (dodate.length > 0) {
        setMessage(
          `Tura je izmenjena: ${dodate.map((s) => s.name).join(', ')}. Pogledaj spisak.`);
      }

      // Keš se upisuje POSLEDNJI, i njegov pad ne sme da pregazi poruku o izmeni
      // ture — ona je vozaču važnija od stanja telefona.
      try {
        storage.saveTour(fresh, accessKey);
      } catch {
        if (dodate.length === 0) {
          setMessage(
            'Telefon je pun — tura se ne pamti za ponovno otvaranje. Uhvati signal '
            + 'da se red isprazni.');
        }
      }
    } catch {
      // Bez signala se ne menja ništa. Tura u telefonu ostaje kakva jeste.
    }
  }, [accessKey]);

  /**
   * Šalje red na server. Briše lokalno samo ono što je server potvrdio.
   *
   * Svaki zapis ide na SVOJU turu, ključem vozača koji ga je napravio — vidi
   * `DriverEvent.tourId`. Do 14.10.2026. je ceo red išao na turu koja je u
   * telefonu, pa je tuđe „tura završena" zatvaralo turu novog vozača.
   */
  const sync = useCallback(async (): Promise<void> => {
    const current = storage.loadTour();
    const grupe = podeliPoTuri(storage.outbox(), current?.id ?? null, accessKey);
    if (grupe.length === 0) return;

    setBusy(true);
    try {
      const poruke: string[] = [];

      for (const grupa of grupe) {
        try {
          // `deviceNow`: po njemu server meri odstupanje sata telefona (D11).
          const { response, prekljuceno, tudja } = await posaljiGrupu(
            fetch, BASE, grupa,
            JSON.stringify({ events: grupa.events.map(zaServer), deviceNow: deviceNow() }),
            accessKey);

          // Zapis drugog vozača na deljenom telefonu, čiji link više ne važi:
          // ostaje njemu, a ovom vozaču se ne javlja kao kvar (K5).
          if (tudja) continue;

          if (!response.ok) {
            // ODBIJEN PAKET SE NE PREĆUTKUJE. Analiza scenarija (K6), 16.10.2026:
            // do tada je vozač video samo broj neposlatog koji raste. Zapisi
            // ostaju u telefonu i šalju se ponovo; vozač mora da zna da javi.
            poruke.push(response.status === 401
              ? 'Server ne prima zapise sa ovog linka. Zapisi su sačuvani u telefonu — javi šefu magacina.'
              : `Server trenutno ne prima zapise (greška ${response.status}). Zapisi su sačuvani u telefonu i šalju se ponovo; ako ovo traje, javi šefu magacina.`);
            throw new Error(`Server je odbio paket (${response.status}).`);
          }

          const result = await response.json();
          storage.forget(result.safeToForget ?? []);

          // Odbijeno se seli u fioku „server nije primio" — u redu bi stajalo
          // zauvek i blokiralo telefon. Vidi `storage.odbij`.
          storage.odbij(result.rejections ?? []);

          // Kupci: da li ih je najava obavestila, i telefoni — osvežava se usput,
          // pa i broj ispravljen u ERP-u stiže do vozača u toku dana (K13).
          if (current && grupa.tourId === current.id && Array.isArray(result.kupci)) {
            const sad = storage.loadTour();
            const novo = sad && sad.id === current.id ? osveziKupce(sad, result.kupci) : null;
            if (novo) {
              try { storage.saveTour(novo, accessKey); } catch { /* pun telefon: ekran ipak */ }
              setTour(novo);
            }
          }

          // Server je ispravio vremena zbog sata telefona: vozač to mora da vidi.
          if (typeof result.clockNote === 'string' && result.clockNote) {
            poruke.push(result.clockNote);
          }
          if (typeof result.serverNow === 'string') {
            setSatPoruka(porukaOSatu(odstupanjeMinuta(result.serverNow, deviceNow())));
          }

          if (result.rejected > 0) {
            poruke.push(result.explanation);
          }

          // Operater je mogao dodati stanicu dok je vozač bio na putu. Sink ide
          // posle svake stanice, pa je ovo najbrži put da izmena stigne do
          // telefona — bez ijednog dodatnog zahteva.
          if (current && grupa.tourId === current.id
              && (prekljuceno
                || trebaPovuciTuru(current.version, result.tourVersion,
                  storage.outboxFor(current.id).length))) {
            // Posle slanja starim zapisima novim ključem tura u telefonu je ona
            // koju je server dao pri otvaranju novog linka — bez tih štrikova.
            // Povlači se ponovo, da vozač vidi svoj rad (K5).
            await pullChanges(current.id);
          }
        } catch {
          // Neuspeh jedne grupe ne zaustavlja ostale. Red ostaje netaknut i
          // pokušaće se ponovo.
        }
      }

      setPending(storage.outbox());
      setOdbijeno(storage.odbijeno());
      setMessage(poruke.length > 0 ? poruke.join(' ') : null);
    } catch {
      // Neuspeh sinhronizacije nije greška vozača i ne prekida mu posao.
      setMessage(null);
    } finally {
      setBusy(false);
    }
  }, [accessKey, pullChanges]);

  // Čim se signal vrati, red se prazni sam. Vozač ne mora ništa da pritisne.
  useEffect(() => {
    if (online && pending.length > 0) void sync();
  }, [online, pending.length, sync]);

  /**
   * Šalje fotografije. Odvojeno od reda zapisa — slika ne sme da zadrži štrik,
   * a štrik ne čeka sliku.
   */
  const [saljemFoto, setSaljemFoto] = useState(false);
  const posaljiFoto = useCallback(async () => {
    setSaljemFoto(true);
    try {
      const ishod = await posaljiFotografije(telefonskoSkladiste, BASE, fetch, accessKey);
      if (ishod.odbijeno.length > 0) {
        storage.odbijFotografije(ishod.odbijeno.map((o) => ({
          id: o.zapis.id, stopId: o.zapis.stopId, takenAt: o.zapis.takenAt, razlog: o.razlog
        })));
        setOdbijeno(storage.odbijeno());
      }
    } catch {
      // Neuspeh slanja nije greška vozača; slika ostaje i šalje se kasnije.
    } finally {
      setSaljemFoto(false);
      await ucitajFotoRed();
    }
  }, [ucitajFotoRed]);

  useEffect(() => {
    if (online && fotoRed.length > 0 && !saljemFoto) void posaljiFoto();
  }, [online, fotoRed.length]); // posaljiFoto i saljemFoto namerno van spiska: šalje se kad dođe signal ili nova slika

  /**
   * Kad se signal vrati a nema šta da se pošalje, pita se samo za verziju.
   *
   * Bez ovoga bi vozač koji ništa ne štiklira — a takvih vožnji ima — ostao na
   * staroj turi do kraja dana, jer se izmena inače saznaje tek uz sink.
   */
  useEffect(() => {
    if (!online || pending.length > 0 || !tour) return;

    let stopped = false;

    fetch(`${BASE}/api/driver/tours/${tour.id}/version?kljuc=${encodeURIComponent(accessKey)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (stopped || !data) return;
        if (typeof data.serverNow === 'string') {
          setSatPoruka(porukaOSatu(odstupanjeMinuta(data.serverNow, deviceNow())));
        }
        if (trebaPovuciTuru(tour.version, data.version, storage.outboxFor(tour.id).length)) {
          void pullChanges(tour.id);
        }
      })
      .catch(() => { /* nema signala; tura ostaje kakva jeste */ });

    return () => { stopped = true; };
  }, [online, pending.length, tour, accessKey, pullChanges]);

  /**
   * @param bezLinka Ne pita se tura iz linka. Posle „Završeno — uzmi sledeću
   *   turu" vozačeva namera je jasna: hoće SLEDEĆU. Link iz SMS-a prve ture je
   *   do 16.10.2026. tu imao prednost, pa je dugme ponovo otvaralo upravo
   *   završenu turu — u krug (analiza scenarija, K20). Pri prvom otvaranju link
   *   i dalje ima prednost: vozač je izričito otvorio baš tu turu.
   */
  const loadTour = async (bezLinka = false) => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(
        `${BASE}/api/driver/tours?kljuc=${encodeURIComponent(accessKey)}`);

      // Neispravan link i nedostupan server nisu ista stvar i ne smeju da
      // izgledaju isto: prvo se rešava porukom šefu, drugo čekanjem signala.
      if (response.status === 401) {
        setMessage('Ovaj link više ne važi. Traži nov od šefa magacina.');
        return;
      }

      const list = await response.json();
      if (!Array.isArray(list)) {
        setMessage('Za danas nema ture na tebe.');
        return;
      }

      // Pravilo je izdvojeno u `izbor.ts` i pod testom — lomilo se već dvaput,
      // i to tiho: vozač otvori link, vidi turu, i nema po čemu da zna da nije
      // ta.
      const { tura: chosen, poruka, izbor: ponudjene } = izaberiTuru(
        list, bezLinka ? null : (tourFromLink ?? null));

      setMessage(poruka);
      setIzbor(ponudjene);

      if (!chosen) {
        return;
      }

      const full: DriverTour = await fetch(
        `${BASE}/api/driver/tours/${chosen.id}?kljuc=${encodeURIComponent(accessKey)}`)
        .then((r) => r.json());

      storage.saveTour(full, accessKey);
      setTour(full);
    } catch {
      // Tura pod drugim linkom se ne pokazuje (deljen telefon), ali se ne krije
      // ni da postoji — vidi tudjaTura.ts (analiza 2.0, D12).
      setMessage(porukaBezVeze({
        imaKesiranuTuru: storage.loadTour() !== null,
        kljucSeRazlikuje: storage.tourDriverKey() !== accessKey
      }));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Otvara turu koju je vozač izabrao sa spiska.
   *
   * Isti put kao i obično učitavanje — samo bez pogađanja koja je.
   */
  const otvoriTuru = async (id: number) => {
    setBusy(true);
    try {
      const full: DriverTour = await fetch(
        `${BASE}/api/driver/tours/${id}?kljuc=${encodeURIComponent(accessKey)}`)
        .then((r) => r.json());

      storage.saveTour(full, accessKey);
      setTour(full);
      setIzbor([]);
      setMessage(null);
    } catch {
      setMessage('Nema veze sa serverom. Probaj ponovo dok imaš signal.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Beleži VIŠE poteza kao jednu izmenu ture.
   *
   * Mora zajedno. Dva uzastopna poziva u istom kliku oba krenu od ture iz svog
   * zatvaranja — React još nije prerenderovao — pa drugi gradi novo stanje nad
   * ONIM PRE prvog i lokalno ga poništi. Događaji bi svejedno otišli na server,
   * ali bi vozaču na ekranu štikliranje nestalo, a ostatak ture bi se računao
   * pogrešno.
   *
   * Red događaja se zbog ovoga ne menja: svaki potez i dalje ide kao svoj
   * zapis, pa prijem na serveru ostaje idempotentan.
   */
  /** Vraća da li je potez stvarno upisan u red — potpis se na punom telefonu ne sme izgubiti ćutke. */
  const zabelezi = (
    events: Omit<DriverEvent, 'clientEventId' | 'occurredAt'>[],
    apply: (tour: DriverTour) => DriverTour
  ): boolean => {
    if (!tour || events.length === 0) return false;

    // PUNO SKLADIŠTE JE STVARAN RIZIK, i to zbog potpisa: svaki je slika u redu
    // za slanje i tamo stoji do sinhronizacije. `storage` namerno ne guta grešku
    // — ovde se hvata, jer bez toga vozač tapne i NIŠTA se ne desi, bez poruke.
    //
    // Dva ishoda se razlikuju, i to je važno:
    //
    //   • red nije primio potez  → ništa se nije zapisalo, potez je izgubljen i
    //     to mu se kaže otvoreno;
    //   • red je primio, a kopija ture nije → posao NIJE izgubljen, server će ga
    //     dobiti pri sinhronizaciji. Ekran se zato ipak osvežava, da pokazuje ono
    //     što je u redu, a vozaču se kaže da je telefon pun.
    let upisani: DriverEvent[];

    try {
      upisani = events.map((e) => storage.enqueue(e));
    } catch {
      setMessage(
        'Telefon je pun i ovaj potez NIJE sačuvan. Uhvati signal da se red isprazni, '
        + 'pa probaj ponovo.');
      return false;
    }

    const next = apply(tour);

    try {
      storage.saveTour(next, accessKey);
    } catch {
      setMessage(
        'Telefon je pun. Potez je sačuvan u redu za slanje i neće se izgubiti, ali se '
        + 'tura ne može zapamtiti — ako zatvoriš aplikaciju pre signala, ekran će '
        + 'izgledati starije nego što jeste.');
    }

    setTour(next);
    setPending((q) => [...q, ...upisani]);
    return true;
  };

  /**
   * Beleži potez: prvo lokalno stanje i red, pa tek onda pokušaj slanja.
   * Redosled je bitan — da veza padne baš u tom trenutku, potez je već sačuvan.
   */
  const record = (
    event: Omit<DriverEvent, 'clientEventId' | 'occurredAt'>,
    apply: (tour: DriverTour) => DriverTour
  ) => zabelezi([event], apply);

  const patchStop = (stopId: number, change: Partial<DriverStop>) => (current: DriverTour): DriverTour => ({
    ...current,
    stops: current.stops.map((s) => (s.id === stopId ? { ...s, ...change } : s))
  });

  /**
   * Vozilo je stalo.
   *
   * ZASTO DUGME, A NE TELEFON
   *   Do 28.09.2026. vozac sa pokvarenim vozilom nije imao sta da pritisne:
   *   izuzetak je vezan za STANICU, a ovde ne pada jedna isporuka nego cela
   *   tura. Zvao je telefonom, pa operater ujutru nije znao ni gde je vozilo
   *   stalo, ni sta je u njemu ostalo.
   *
   * Radi i bez mreze: zapis ceka u redu kao i svaki drugi. Kad signala ima,
   * salje se odmah — kvar je jedina stvar na ovom ekranu koja ne trpi cekanje.
   */
  const javiKvar = () => {
    record(
      { type: EventType.VehicleBrokeDown, text: kvarTekst.trim() || undefined },
      (current) => ({ ...current, breakdownReportedAt: new Date().toISOString() }));

    setKvarOtvoren(false);
    setKvarTekst('');

    if (online) void sync();
  };

  const arrive = (stop: DriverStop) =>
    record({ type: EventType.ArrivedAtStop, stopId: stop.id },
      patchStop(stop.id, { actualArrival: new Date().toISOString() }));

  const depart = (stop: DriverStop) => {
    record({ type: EventType.DepartedFromStop, stopId: stop.id },
      patchStop(stop.id, { actualDeparture: new Date().toISOString() }));

    posleOdlaska(stop);
  };

  /**
   * Posle odlaska se otvara sledeća stanica.
   *
   * Vozač inače zatvara ovu pa traži svoju među četrnaest — a tura je niz, ne
   * spisak za pretraživanje.
   */
  const posleOdlaska = (stop: DriverStop) => {
    setPitanjeOOstatku(null);

    const ostale = tour?.stops
      .filter((s) => !s.removedAt && !s.actualDeparture && s.id !== stop.id)
      .sort((a, b) => a.sequence - b.sequence) ?? [];

    setOpenStopId(ostale.length > 0 ? ostale[0].id : null);
  };

  /**
   * Odlazak sa stanice na kojoj je nešto ostalo.
   *
   * Do 11.09.2026. je ovo prolazilo nemo: „Odlazim sa stanice" se puštalo sa
   * 0/3 štiklirano, tura se zatvarala, i roba je do sledećeg jutra bila
   * nevidljiva. Operater ju je ujutru zaticao kao spisak artikala BEZ IJEDNOG
   * RAZLOGA — a jedini čovek koji je znao zašto nije predata bio je vozač, sat
   * vremena ranije.
   *
   * Zato se pita. Ne blokira: svaki odgovor vodi ka odlasku, uključujući i
   * „zaboravio sam da štikliram", koji je izmereno najčešći.
   */
  /**
   * Da li pre odlaska treba pitati za potpis.
   *
   * Pravilo stize UZ TURU, pa vazi i bez mreze. Ne pita se kad nema sta da se
   * potpise: preuzimanje se ne potpisuje (roba se uzima, ne predaje), potpisana
   * stanica se ne pita drugi put, a stanica sa koje nista nije predato nema
   * isporuku za koju bi potpis bio dokaz.
   */
  const traziPotpis = (stop: DriverStop): boolean =>
    !stop.isPickup
    && !stop.hasSignature
    && tour?.signatureRule !== 'ne_trazi'
    && stop.items.some((i) => i.isDelivered);

  const krecem = (stop: DriverStop) => {
    if (trebaPitatiZaOstatak(stop)) {
      setPitanjeOOstatku(stop.id);
      return;
    }

    if (traziPotpis(stop)) {
      setPitanjeOPotpisu(stop.id);
      return;
    }

    depart(stop);
  };

  /**
   * Odlazak bez potpisa, sa razlogom.
   *
   * Razlog ide u belesku stanice, jednim upisom sa odlaskom — razdvojeno bi drugi
   * potez ponistio prvi (vidi `zabelezi`).
   */
  const bezPotpisaIKreni = (stop: DriverStop, razlog: string) => {
    const tekst = `Bez potpisa: ${razlog}`;

    zabelezi(
      [
        { type: EventType.NoteAdded, stopId: stop.id, text: tekst },
        { type: EventType.DepartedFromStop, stopId: stop.id }
      ],
      patchStop(stop.id, {
        notes: stop.notes ? `${stop.notes}
${tekst}` : tekst,
        actualDeparture: new Date().toISOString()
      }));

    setPitanjeOPotpisu(null);
    posleOdlaska(stop);
  };

  /**
   * Pogrešan štrik se vraća — dok vozač ne zatvori turu.
   *
   * Odluka vlasnika, 14.10.2026 (analiza 2.0): do tada ga nije mogao vratiti
   * niko, a stavka je važila kao predata. Upis u ERP čeka zatvaranje ture, pa
   * je vraćanje do tada bezbedno.
   */
  const [vracam, setVracam] = useState<{ stopId: number; itemId: number } | null>(null);

  const vratiStavku = (stop: DriverStop, itemId: number) => {
    record({ type: EventType.ItemUndelivered, stopId: stop.id, stopItemId: itemId }, (current) => ({
      ...current,
      stops: current.stops.map((s) =>
        s.id === stop.id
          ? {
            ...s,
            items: s.items.map((i) => (i.id === itemId
              ? { ...i, isDelivered: false, deliveredAt: null }
              : i))
          }
          : s
      )
    }));
    setVracam(null);
  };

  const deliver = (stop: DriverStop, itemId: number) =>
    record({ type: EventType.ItemDelivered, stopId: stop.id, stopItemId: itemId }, (current) => ({
      ...current,
      stops: current.stops.map((s) =>
        s.id === stop.id
          ? { ...s, items: s.items.map((i) => (i.id === itemId ? { ...i, isDelivered: true } : i)) }
          : s
      )
    }));

  /**
   * SVE SA STANICE ODJEDNOM.
   *
   * Izmereno 11.09.2026: prosečna stanica u prodavnici nosi 5,4 stavke, najveća
   * 28. Niko ne kucka dvadeset osam kućica dok istovara kombi — pa se nije
   * kuckalo nijedna: od 318 napuštenih stanica, 35 je ostalo sa neštikliranim
   * stavkama, a 28 od njih bez ijednog razloga. Same prodavnice: 14 stanica i
   * 100 komada koji su fizički predati, a u aplikaciji stoje kao neisporučeni.
   *
   * Zato je predaja cele stanice JEDAN potez, a kućica po kućica ostaje za
   * izuzetak — „predato je sve osim jednog", što je ređi slučaj.
   *
   * Red događaja se ne menja: svaka stavka i dalje šalje svoj `ItemDelivered`,
   * pa server ne zna da se nešto promenilo i prijem ostaje idempotentan.
   * Upisuje se odjednom jer `record` primenjuje izmenu na turu iz svog zatvaranja
   * — deset poziva u petlji bi deset puta krenulo od ISTE ture i sačuvalo samo
   * poslednju stavku.
   */
  const predajSve = (stop: DriverStop) =>
    zabelezi(predajaCeleStanice(stop), svePredato(stop.id));

  /** Po jedan zapis o predaji za svaku stavku koja još nije predata. */
  const predajaCeleStanice = (stop: DriverStop) =>
    stop.items
      .filter((i) => !i.isDelivered)
      .map((item) => ({
        type: EventType.ItemDelivered, stopId: stop.id, stopItemId: item.id
      }));

  const svePredato = (stopId: number) => (current: DriverTour): DriverTour => ({
    ...current,
    stops: current.stops.map((s) => (s.id === stopId
      ? { ...s, items: s.items.map((i) => ({ ...i, isDelivered: true })) }
      : s))
  });

  /**
   * „Ipak sam sve predao" — predaja cele stanice i odlazak, jednim upisom.
   * Razdvojeno bi drugi potez poništio prvi; vidi `zabelezi`.
   */
  const predajSveIKreni = (stop: DriverStop) => {
    // POTPIS SE NE PRESKAČE. Analiza 2.0, 14.10.2026: ovo dugme — izmereno
    // najčešći odgovor — beležilo je odlazak odmah, pa je pravilo „obavezan"
    // prolazilo bez potpisa. Kad posle predaje potpis treba, predaja se beleži,
    // a odlazak ide kroz isto pitanje kao „Krećem".
    const posle: DriverStop = { ...stop, items: stop.items.map((i) => ({ ...i, isDelivered: true })) };

    if (traziPotpis(posle)) {
      predajSve(stop);
      setPitanjeOOstatku(null);
      setPitanjeOPotpisu(stop.id);
      return;
    }

    zabelezi(
      [...predajaCeleStanice(stop), { type: EventType.DepartedFromStop, stopId: stop.id }],
      (current) => patchStop(stop.id, { actualDeparture: new Date().toISOString() })(
        svePredato(stop.id)(current)));

    posleOdlaska(stop);
  };

  /** Razlog pa odlazak, takođe jednim upisom. */
  const razlogIKreni = (stop: DriverStop, reason: string) => {
    // Isto kao gore: ako je sa stanice nešto predato, potpis za TO se traži
    // pre odlaska — razlog za ostatak ga ne zamenjuje.
    if (traziPotpis(stop)) {
      flagException(stop, reason);
      setPitanjeOOstatku(null);
      setPitanjeOPotpisu(stop.id);
      return;
    }

    zabelezi(
      [
        { type: EventType.StopException, stopId: stop.id, text: reason },
        { type: EventType.DepartedFromStop, stopId: stop.id }
      ],
      patchStop(stop.id, {
        exceptionReason: reason,
        actualDeparture: new Date().toISOString()
      }));

    posleOdlaska(stop);
  };

  const flagException = (stop: DriverStop, reason: string) =>
    record({ type: EventType.StopException, stopId: stop.id, text: reason },
      patchStop(stop.id, { exceptionReason: reason }));

  /**
   * Beleška se piše U APLIKACIJI, ne u prozorčiću pregledača.
   *
   * Do 11.09.2026. je ovo išlo kroz `window.prompt`. Taj prozorčić neki Android
   * pregledači blokiraju — dugme tada ne uradi ništa, ekran se ne pomeri, i
   * vozač zaključi da je aplikacija pukla. Uz to ne poštuje veličinu slova, pa
   * na nekim telefonima izađe sitan.
   */
  const sacuvajBelesku = (stop: DriverStop) => {
    const tekst = beleska.trim();
    if (!tekst) return;

    record({ type: EventType.NoteAdded, stopId: stop.id, text: tekst },
      patchStop(stop.id, { notes: stop.notes ? `${stop.notes}\n${tekst}` : tekst }));

    setBeleskaZa(null);
    setBeleska('');
  };

  const sign = (stop: DriverStop, dataUrl: string, ocena: OcenaIzPotpisa | null = null) => {
    // Kućica se zatvara SAMO kad je potpis upisan u red. Na punom telefonu je
    // ranije nestajao bez traga — kućica se zatvorila, a potpis nije sačuvan
    // (analiza 2.0, D13). Sad ostaje otvorena, sa porukom šta da se uradi.
    // Ocena ide u ISTOM paketu sa potpisom: server broji samo ocenu koja je
    // stigla uz potpis, pa se ne može dati posle, mimo kupca.
    const dogadjaji: Omit<DriverEvent, 'clientEventId' | 'occurredAt'>[] = [
      { type: EventType.SignatureCaptured, stopId: stop.id, signature: dataUrl }
    ];
    if (ocena) {
      dogadjaji.push({
        type: EventType.CustomerRated, stopId: stop.id, rating: ocena.zvezdica, text: ocena.komentar
      });
    }
    const upisano = zabelezi(dogadjaji, patchStop(stop.id, { hasSignature: true }));
    if (upisano) {
      setSigningStopId(null);
      setPotpisGreska(null);
    } else {
      // Kućica pokriva ceo ekran, pa poruka ispod nje nije vidljiva (16.10.2026).
      setPotpisGreska('Telefon je pun i potpis NIJE sačuvan. Uhvati signal da se red '
        + 'isprazni, pa potpiši ponovo.');
    }
  };

  /**
   * Promena redosleda uvek nosi razlog. To je ceo smisao ovog zapisa.
   *
   * Razlog se BIRA, ne kuca. Do 11.09.2026. je `window.prompt` ispisivao četiri
   * ponuđena razloga i tražio da vozač jedan od njih otkuca rukom — jedino
   * mesto na celom ekranu gde se kuca, i to na kiši. Svuda drugde je razlog
   * jedan tap, pa je i ovde.
   */
  const moveUp = (stop: DriverStop, reason: string) => {
    if (!tour) return;

    const ordered = [...tour.stops].sort((a, b) => a.sequence - b.sequence);
    const index = ordered.findIndex((s) => s.id === stop.id);
    if (index <= 0) return;

    setPitanjeORedosledu(null);

    const above = ordered[index - 1];
    const newSequence = above.sequence;

    record({
      type: EventType.StopReordered,
      stopId: stop.id,
      sequence: newSequence,
      text: reason
    }, (current) => ({
      ...current,
      stops: current.stops.map((s) => {
        if (s.id === stop.id) return { ...s, sequence: newSequence };
        if (s.id === above.id) return { ...s, sequence: stop.sequence };
        return s;
      })
    }));
  };

  const startTour = () =>
    record({ type: EventType.TourStarted }, (current) => ({
      ...current,
      actualStart: new Date().toISOString(),
      status: 'InProgress'
    }));

  const finishTour = () =>
    record({ type: EventType.TourFinished }, (current) => ({
      ...current,
      actualEnd: new Date().toISOString(),
      status: 'Completed'
    }));

  const stops = useMemo(
    () => (tour ? [...tour.stops].sort((a, b) => a.sequence - b.sequence) : []),
    [tour]
  );

  /**
   * Koliko je stanica završeno i koja je sledeća — vozačev jedini kontekst.
   *
   * Skinuta stanica se ne broji ni u jedno ni u drugo: ona se ne vozi, ali se
   * i dalje vidi na spisku.
   */
  const zive = stops.filter((s) => !s.removedAt);

  /**
   * Stanice o kojima vozač još nije rekao ništa.
   *
   * ZAŠTO NE SAMO „NIJE OTIŠAO"
   *   Dok je uslov bio samo odlazak, „Završi turu" se nije ni pojavljivalo dok
   *   sve stanice nisu zatvorene — pa je vozač sa 6/9 prosto zatvorio
   *   aplikaciju, a operater turu zatvarao sutradan. Izmereno 14.09.2026, tura
   *   65: šest od devet zatvorenih, a sve razvezeno.
   *
   *   Sad se traži ISHOD, ne odlazak: stanica je rešena kad je vozač otišao sa
   *   nje, kad je prijavio izuzetak, ili kad je odgovorio pri zatvaranju.
   */
  const neresene = zive.filter(
    (s) => !s.actualDeparture && !s.exceptionReason && !s.deliveryConfirmedAt);

  /**
   * Zatvaranje stanice odgovorom na kraju ture.
   *
   * Vremena se NE diraju: odgovor stiže u magacinu, ne na stanici. Upisati
   * tadašnje vreme kao odlazak značilo bi da je isporuka trajala sat i po — a po
   * tim razmacima se računaju procene za sledeće ture.
   */
  const zatvoriNaKraju = (stop: DriverStop, odgovor: string, predato: boolean) => {
    const dogadjaji: Omit<DriverEvent, 'clientEventId' | 'occurredAt'>[] = [];

    if (predato) {
      dogadjaji.push(...predajaCeleStanice(stop));
    } else {
      dogadjaji.push({ type: EventType.StopException, stopId: stop.id, text: odgovor });
    }

    dogadjaji.push({ type: EventType.StopClosedAtEnd, stopId: stop.id, text: odgovor });

    const izmena: Partial<DriverStop> = predato
      ? { deliveryConfirmedAt: deviceNow() }
      : { deliveryConfirmedAt: deviceNow(), exceptionReason: odgovor };

    zabelezi(dogadjaji, (current) =>
      patchStop(stop.id, izmena)(predato ? svePredato(stop.id)(current) : current));

    setZatvaramTuru(true);
  };

  const gotovih = zive.filter((s) => s.actualDeparture !== null).length;
  const sledeca = zive.find((s) => s.actualDeparture === null);

  const remaining = neresene.length;

  const ostatak = useMemo(() => ostatakTure(zive), [zive]);

  if (!tour) {
    return (
      <div className="driver">
        <header className="driver__bar">
          <span>Vozač</span>
          <span className={online ? 'dot dot--on' : 'dot dot--off'}>{online ? 'mreža' : 'bez mreže'}</span>
        </header>

        <div className="driver__empty">
          <p>Turu učitaj dok imaš signal, pre polaska. Posle toga ti mreža ne treba.</p>
          <button type="button" className="primary" onClick={() => void loadTour()} disabled={busy}>
            {busy ? 'Učitavam…' : 'Učitaj današnju turu'}
          </button>

          {/* Tuđa tura se ne prikazuje, ali se ni ne prećutkuje: dok je njen red
              neposlat, ovaj telefon nosi tuđi posao i to niko ne sme da obriše
              slučajno. Sinhronizacija ga i dalje prazni sama. */}
          {pending.length > 0 && (
            <p className="driver__message">
              Na ovom telefonu stoji tura drugog vozača sa {pending.length} neposlatih
              zapisa. Šalju se sami čim ima signala — ne briši podatke pregledača.
            </p>
          )}

          {satPoruka && <div className="health health--bad">{satPoruka}</div>}

          {message && <p className="driver__message">{message}</p>}

          {/* Dve nezavrsene ture: ne pogadja se umesto vozaca. Dugmad su velika
              jer se ovo pritiska u kombiju, u zurbi i cesto sa rukavicama. */}
          {izbor.length > 0 && (
            <ul className="driver__izbor">
              {izbor.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className="primary"
                    disabled={busy}
                    onClick={() => void otvoriTuru(t.id)}
                  >
                    {t.name ?? `Tura ${t.id}`}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="driver">
      <header className="driver__bar">
        <span>
          {tour.name} · {tour.vehicle}
          {/* Tura od ranijeg dana se od 28.09.2026. otvara i posle svog dana —
              da bi mogla da se zatvori. Vozac tada MORA da vidi koji je to dan,
              inace izgleda kao danasnji posao. */}
          {!jeDanasnja(tour.date) && (
            <span className="driver__old">
              {' '}· od {new Date(tour.date).toLocaleDateString('sr-RS')}
            </span>
          )}
        </span>
        <span className={online ? 'dot dot--on' : 'dot dot--off'}>{online ? 'mreža' : 'bez mreže'}</span>
      </header>

      <Pomoc ekran="vozac" />

      {/* Broj nesinhronizovanih zapisa stoji stalno na ekranu. Vozač mora znati
          da nešto još nije otišlo, pre nego što ugasi telefon. */}
      {(pending.length > 0 || fotoRed.length > 0) && (
        <div className="driver__pending">
          {pending.length > 0 && <>{pending.length} {pending.length === 1 ? 'zapis' : 'zapisa'} još nije poslato</>}
          {pending.length > 0 && fotoRed.length > 0 && ' · '}
          {fotoRed.length > 0 && <>{fotoRed.length} {fotoRed.length % 10 >= 2 && fotoRed.length % 10 <= 4 && (fotoRed.length % 100 < 12 || fotoRed.length % 100 > 14) ? 'fotografije' : 'fotografija'} još nije poslato</>}
          <button
            type="button"
            onClick={() => { void sync(); void posaljiFoto(); }}
            disabled={busy || saljemFoto || !online}
          >
            {busy || saljemFoto ? 'Šaljem…' : 'Pošalji sad'}
          </button>
        </div>
      )}

      {/* Ono što server NIJE primio. Stoji odvojeno od neposlatog: ponovno
          slanje tu ne pomaže, pomaže samo da šef zna. */}
      {odbijeno.length > 0 && (
        <div className="driver__message">
          <strong>
            {odbijeno.length} {odbijeno.length === 1 ? 'zapis server nije primio' : 'zapisa server nije primio'}
          </strong>
          {' '}— javi šefu magacina šta piše:
          <ul>
            {Array.from(new Set(odbijeno.map((o) => o.razlog))).map((r) => <li key={r}>{r}</li>)}
          </ul>
          <button
            type="button"
            onClick={() => { storage.ocistiOdbijeno(); setOdbijeno([]); }}
          >
            Javio sam — skloni
          </button>
        </div>
      )}

      {message && <p className="driver__message">{message}</p>}

      {/* Gde je vozač u turi. Bez ovoga mora da broji stanice da bi znao
          koliko je ostalo — a to je jedino što ga zanima dok vozi. */}
      {tour.actualStart && !tour.actualEnd && (
        <div className="driver__progress">
          <div className="driver__progress-bar">
            {/* Samo stanice koje se voze: skinuta ostaje precrtana na spisku,
                ali ne ulazi u brojač — sa njom tura nikad nije bila 100 %
                (analiza 2.0, D13). */}
            <span style={{ width: `${Math.round(gotovih / Math.max(zive.length, 1) * 100)}%` }} />
          </div>
          <p>
            <strong>{gotovih} od {zive.length}</strong> stanica
            {sledeca && <> · sledeća: <strong>{sledeca.name}</strong></>}
            {!sledeca && gotovih === zive.length && <> · sve gotovo, vrati se u magacin</>}
          </p>
        </div>
      )}

      {/* Pregled dana. Crta se iz ture koja je već u telefonu, pa radi i na
          putu — prava mapa bi tražila pločice sa interneta. */}
      <button
        type="button"
        className="driver__sketch-toggle"
        aria-expanded={showSketch}
        onClick={() => setShowSketch((v) => !v)}
      >
        {showSketch ? 'Sakrij mapu dana' : 'Mapa dana'}
      </button>

      {showSketch && <TourSketch tour={tour} />}

      {!tour.actualStart && (
        <button type="button" className="primary driver__start" onClick={startTour}>
          Krenuo sam — {tour.start.name}
        </button>
      )}

      {navigatingTo && (
        <div className="dnav">
          <div className="dnav__card">
            <h2>Otvara se navigacija</h2>

            <p>
              Mape se otvaraju preko cele slike i ovaj ekran se skloni. <strong>Tura
              nije nestala</strong> — čeka te.
            </p>

            <p className="dnav__how">
              Za povratak: pritisni dugme <strong>nazad</strong> na telefonu, ili
              dvaput dugme za prebacivanje aplikacija pa izaberi pregledač.
            </p>

            <p className="muted">
              Sve što si štiklirao je sačuvano u telefonu i ostaje sačuvano i bez
              signala.
            </p>

            <a
              className="dnav__go"
              href={mapsLink(navigatingTo)}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                storage.markNavigationExplained();
                setNavigatingTo(null);
              }}
            >
              Kreni, razumeo sam
            </a>

            <button type="button" className="link" onClick={() => setNavigatingTo(null)}>
              Ne još
            </button>
          </div>
        </div>
      )}

      <ol className="driver__stops">
        {stops.map((stop, index) => {
          const open = openStopId === stop.id;
          const done = stop.actualDeparture !== null;
          const delivered = stop.items.filter((i) => i.isDelivered).length;

          return (
            <li
              key={stop.id}
              className={`dstop${done ? ' dstop--done' : ''}`
                + `${stop.isPickup ? ' dstop--uzimanje' : ''}`
                + `${stop.removedAt ? ' dstop--skinuto' : ''}`}
            >
              <button
                type="button"
                className="dstop__head"
                onClick={() => setOpenStopId(open ? null : stop.id)}
              >
                <span className="dstop__no">{index + 1}</span>
                <span className="dstop__name">
                  {/* Preuzimanje se mora razlikovati na prvi pogled: sve ostalo
                      na ovom ekranu je isporuka, pa bi vozač inače pokušao da
                      ostavi robu tamo gde treba da je uzme. */}
                  {stop.isPickup && <span className="dstop__tag">UZIMA SE</span>}
                  {/* Stanica koja je stigla naknadno mora da se vidi kao nova.
                      Bez oznake izgleda kao da je oduvek bila na spisku, pa
                      vozač misli da ju je prevideo — a nije. */}
                  {stop.addedAt && !stop.removedAt && (
                    <span className="dstop__tag dstop__tag--novo">DODATO</span>
                  )}
                  {stop.removedAt && <span className="dstop__tag dstop__tag--skinuto">OTKAZANO</span>}
                  {stop.name}
                  <span className="dstop__sub">
                    {stop.city ?? ''} · {delivered}/{stop.items.length}{' '}
                    {stop.isPickup ? 'utovareno' : 'isporučeno'}
                    {stop.requiresTwoPeople && <strong> · dva čoveka</strong>}
                  </span>
                </span>
                <span className="dstop__state">{done ? '✓' : open ? '▾' : '▸'}</span>
              </button>

              {open && (
                <div className="dstop__body">
                  {/* Prvo što se vidi kad se stanica otvori: tačka nije kapija.
                      Traženje brojeva u ulici koja ih nema je izgubljeno vreme,
                      a telefon je ovde jedino što dovodi do kupca. */}
                  {stop.removedAt && (
                    <p className="dstop__skinuto">
                      NE ISPORUČUJ. Ova stanica je skinuta sa ture
                      {stop.removedReason ? `: ${stop.removedReason}` : '.'}
                      {' '}Roba ostaje u vozilu — šef magacina odlučuje šta dalje.
                    </p>
                  )}

                  {stop.addedAt && !stop.removedAt && (
                    <p className="dstop__novo">
                      Ova stanica je dodata posle polaska
                      {stop.addedReason ? `: ${stop.addedReason}` : '.'}
                    </p>
                  )}

                  {stop.withoutHouseNumber && (
                    <p className="dstop__bb">
                      <strong>BEZ BROJA (bb)</strong> — tačka je na ulici, ne pred kućom.
                      Pozovi kupca.
                    </p>
                  )}

                  {/* Kupac ne zna da danas dolaziš: SMS mu nije prošao, najčešće
                      zato što u ERP-u ima samo fiksni broj. Stoji odmah uz „bb"
                      jer se rešava istim potezom — pozovi ga, i to ranije nego
                      obično. */}
                  {stop.customerNotAnnounced && (
                    <p className="dstop__bb">
                      <strong>KUPAC NIJE OBAVEŠTEN</strong> — SMS da roba stiže danas
                      nije prošao. Ne očekuje te. Pozovi ga pre nego što dođeš.
                    </p>
                  )}

                  {(stop.floor !== null || stop.accessNotes) && (
                    <p className="dstop__access">
                      {stop.floor !== null && stop.floor > 0 && (
                        <>
                          {stop.floor}. sprat{stop.hasLift ? ', ima lift' : ', bez lifta'}
                          {stop.accessNotes ? ' · ' : ''}
                        </>
                      )}
                      {stop.accessNotes}
                    </p>
                  )}

                  <div className="dstop__links">
                    {stop.latitude !== null && stop.longitude !== null && (
                      storage.navigationExplained() ? (
                        <a
                          href={mapsLink(stop)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Navigacija
                        </a>
                      ) : (
                        <button type="button" onClick={() => setNavigatingTo(stop)}>
                          Navigacija
                        </button>
                      )
                    )}

                    {/* Poziv radi i bez mreže — brojevi su učitani sa turom.
                        Kad ih ima više, drugi je nekog drugog ukućanina. */}
                    {stop.phones?.map((number, i) => (
                      <a key={number} href={`tel:${number}`}>
                        {stop.phones.length > 1 ? `Pozovi ${i + 1}` : 'Pozovi kupca'}
                      </a>
                    ))}
                  </div>

                  {/* Skinuta stanica nema nijedno dugme. Ostaje da se vidi, ali
                      se na njoj nema šta raditi — inače bi je vozač štiklirao i
                      isporučio robu koja je otkazana. */}
                  {/* ROBA SE VIDI UVEK, I PRE DOLASKA.
                      Do 11.09.2026. je spisak stajao unutar grane „posle
                      dolaska" — vozač nije mogao da vidi šta nosi dok ne
                      pritisne „Stigao sam". A to je tačno ono što mu treba
                      RANIJE: dok pakuje kombi i dok zvoni na vrata. Pre dolaska
                      se samo ne štiklira. */}
                  {stop.removedAt ? null : (
                    <>
                      <ul className="dstop__items">
                        {stop.items.map((item) => (
                          <li key={item.id}>
                            <label>
                              {/* Štrik se sme vratiti dok vozač ne zatvori turu
                                  (odluka vlasnika 14.10.2026) — pita se pre toga,
                                  da ga ne vrati dodir u džepu. */}
                              <input
                                type="checkbox"
                                checked={item.isDelivered}
                                disabled={!stop.actualArrival || (item.isDelivered && !!tour.actualEnd)}
                                onChange={() => (item.isDelivered
                                  ? setVracam({ stopId: stop.id, itemId: item.id })
                                  : deliver(stop, item.id))}
                              />
                              {item.articleName} <span className="muted">× {item.quantity}</span>
                              {/* Roba za kupca koji dolazi u objekat — da se ne
                                  pomeša sa dopunom za policu. */}
                              {item.zaKupca && <span className="flag"> za kupca</span>}
                            </label>

                            {vracam?.stopId === stop.id && vracam.itemId === item.id && (
                              <span className="dstop__vrati">
                                Nije predato?{' '}
                                <button type="button" onClick={() => vratiStavku(stop, item.id)}>
                                  Da, vrati
                                </button>{' '}
                                <button type="button" className="link" onClick={() => setVracam(null)}>
                                  Ne
                                </button>
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>

                      {!stop.actualArrival && (
                        <p className="dstop__cekaj">
                          {stop.items.length}{' '}
                          {stop.items.length === 1 ? 'stavka' : 'stavki'}
                          {stop.isPickup ? ' za utovar' : ' za ovu adresu'}.
                          {' '}Štiklira se posle „Stigao sam".
                        </p>
                      )}
                    </>
                  )}

                  {stop.removedAt ? null : !stop.actualArrival ? (
                    <button type="button" className="primary" onClick={() => arrive(stop)}>
                      Stigao sam
                    </button>
                  ) : (
                    <>
                      {/* Cela stanica jednim potezom. Kućica po kućica ostaje za
                          slučaj „predato je sve osim jednog". */}
                      {stop.items.some((i) => !i.isDelivered) && (
                        <button
                          type="button"
                          className="primary dstop__sve"
                          onClick={() => predajSve(stop)}
                        >
                          {stop.isPickup ? 'Utovario sam sve' : 'Predao sam sve'}
                          {' '}({stop.items.filter((i) => !i.isDelivered).length})
                        </button>
                      )}

                      {/* Preuzimanje niko ne potpisuje — roba se ne predaje
                          nego uzima, a potpis je dokaz o isporuci. */}
                      {!stop.isPickup && (stop.hasSignature ? (
                        <p className="dstop__signed">Potpis prikupljen ✓</p>
                      ) : (
                        <button type="button" onClick={() => setSigningStopId(stop.id)}>
                          Uzmi potpis
                        </button>
                      ))}

                      {beleskaZa === stop.id ? (
                        <div className="dstop__beleska">
                          <label htmlFor={`beleska-${stop.id}`}>Beleška za ovu stanicu</label>
                          <textarea
                            id={`beleska-${stop.id}`}
                            rows={3}
                            value={beleska}
                            maxLength={500}
                            onChange={(e) => setBeleska(e.target.value)}
                            placeholder="npr. ostavljeno kod komšije na broju 12"
                            autoFocus
                          />
                          <div className="dstop__beleska-dugmad">
                            <button
                              type="button"
                              className="primary"
                              disabled={beleska.trim() === ''}
                              onClick={() => sacuvajBelesku(stop)}
                            >
                              Sačuvaj belešku
                            </button>
                            <button
                              type="button"
                              className="link"
                              onClick={() => { setBeleskaZa(null); setBeleska(''); }}
                            >
                              Odustani
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setBeleskaZa(stop.id); setBeleska(''); }}
                        >
                          Beleška
                        </button>
                      )}

                      <FotografijeStanice
                        tourId={tour.id}
                        accessKey={accessKey}
                        stopId={stop.id}
                        naCekanju={fotoRed.filter((f) => f.stopId === stop.id && f.tourId === tour.id).length}
                        onDodato={() => void ucitajFotoRed()}
                      />

                      {stop.exceptionReason ? (
                        <p className="dstop__exception">{stop.exceptionReason}</p>
                      ) : (
                        <div className="dstop__exceptions">
                          {(stop.isPickup ? PICKUP_EXCEPTIONS : EXCEPTIONS).map((reason) => (
                            <button key={reason} type="button" onClick={() => flagException(stop, reason)}>
                              {reason}
                            </button>
                          ))}
                        </div>
                      )}

                      {!stop.actualDeparture
                        && pitanjeOOstatku !== stop.id
                        && pitanjeOPotpisu !== stop.id && (
                        <button type="button" className="primary" onClick={() => krecem(stop)}>
                          Odlazim sa stanice
                        </button>
                      )}

                      {/* POTPIS PRE ODLASKA.
                          Nije brana nego pitanje — osim kad je pravilo „obavezan",
                          i tada se to jasno kaže. Izmereno 28.09.2026: potpis je
                          stajao na 61% isporuka, a aplikacija ga nije tražila
                          nigde. */}
                      {!stop.actualDeparture && pitanjeOPotpisu === stop.id && (
                        <div className="dstop__ostatak">
                          <p className="dstop__ostatak-pitanje">
                            {tour.signatureRule === 'obavezan'
                              ? <>Potpis je <strong>obavezan</strong> — bez njega se stanica ne zatvara.</>
                              : <>Nema potpisa kupca. Uzmi ga, ili reci zašto ne može.</>}
                          </p>

                          <button
                            type="button"
                            className="primary"
                            onClick={() => { setPitanjeOPotpisu(null); setSigningStopId(stop.id); }}
                          >
                            Uzmi potpis
                          </button>

                          {tour.signatureRule !== 'obavezan' && BEZ_POTPISA.map((razlog) => (
                            <button
                              key={razlog}
                              type="button"
                              onClick={() => bezPotpisaIKreni(stop, razlog)}
                            >
                              {razlog}
                            </button>
                          ))}

                          <button
                            type="button"
                            className="link"
                            onClick={() => setPitanjeOPotpisu(null)}
                          >
                            Nazad
                          </button>
                        </div>
                      )}

                      {/* Pita se samo kad nešto stvarno ostaje, i svaki odgovor
                          vodi ka odlasku — ovo nije brana nego zapis. */}
                      {!stop.actualDeparture && pitanjeOOstatku === stop.id && (
                        <div className="dstop__ostatak">
                          <p className="dstop__ostatak-pitanje">
                            Ostaje{' '}
                            <strong>
                              {stop.items.filter((i) => !i.isDelivered).length}{' '}
                              {stop.items.filter((i) => !i.isDelivered).length === 1
                                ? 'stavka'
                                : 'stavki'}
                            </strong>
                            . Šta sa tim?
                          </p>

                          <button
                            type="button"
                            className="primary"
                            onClick={() => predajSveIKreni(stop)}
                          >
                            {stop.isPickup ? 'Ipak sam sve utovario' : 'Ipak sam sve predao'}
                          </button>

                          {(stop.isPickup ? PICKUP_EXCEPTIONS : EXCEPTIONS).map((reason) => (
                            <button
                              key={reason}
                              type="button"
                              onClick={() => razlogIKreni(stop, reason)}
                            >
                              {reason}
                            </button>
                          ))}

                          <button
                            type="button"
                            onClick={() => razlogIKreni(stop, OSTAJE_U_VOZILU)}
                          >
                            {OSTAJE_U_VOZILU}
                          </button>

                          <button
                            type="button"
                            className="link"
                            onClick={() => setPitanjeOOstatku(null)}
                          >
                            Nazad — još nisam gotov
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {index > 0 && !stop.actualArrival && pitanjeORedosledu !== stop.id && (
                    <button
                      type="button"
                      className="link"
                      onClick={() => setPitanjeORedosledu(stop.id)}
                    >
                      Idem prvo ovde
                    </button>
                  )}

                  {/* Razlog se bira, ne kuca. Isti oblik kao izuzetci — vozač
                      ovo radi na kiši, jednom rukom. */}
                  {index > 0 && !stop.actualArrival && pitanjeORedosledu === stop.id && (
                    <div className="dstop__redosled">
                      <p className="dstop__redosled-pitanje">Zašto ideš prvo ovde?</p>

                      {REORDER_REASONS.map((reason) => (
                        <button
                          key={reason}
                          type="button"
                          onClick={() => moveUp(stop, reason)}
                        >
                          {reason}
                        </button>
                      ))}

                      <button
                        type="button"
                        className="link"
                        onClick={() => setPitanjeORedosledu(null)}
                      >
                        Odustani
                      </button>
                    </div>
                  )}

                  {stop.notes && <p className="dstop__notes">{stop.notes}</p>}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* VOZILO STALO. Stoji dok tura traje — pritiska se pored puta, pa ne sme
          da se trazi po ekranu, ali ne sme ni da smeta. */}
      {tour.actualStart && !tour.actualEnd && !zatvaramTuru && (
        tour.breakdownReportedAt ? (
          <p className="driver__kvar-javljen">
            Javljeno da je vozilo stalo u{' '}
            {new Date(tour.breakdownReportedAt).toLocaleTimeString('sr-RS',
              { hour: '2-digit', minute: '2-digit' })}
            . Sef to vidi na svom ekranu.
            {' '}
            <button type="button" className="link" onClick={() => setKvarOtvoren(true)}>
              Javi ponovo
            </button>
          </p>
        ) : !kvarOtvoren && (
          <button type="button" className="driver__kvar" onClick={() => setKvarOtvoren(true)}>
            Vozilo stalo
          </button>
        )
      )}

      {kvarOtvoren && (
        <div className="driver__kvar-polje">
          <label htmlFor="kvar">Sta je sa vozilom? (nije obavezno)</label>
          <textarea
            id="kvar"
            rows={2}
            value={kvarTekst}
            onChange={(e) => setKvarTekst(e.target.value)}
            placeholder="npr. pukla guma kod Odzaka"
            autoFocus
          />
          <div className="dstop__beleska-dugmad">
            <button type="button" className="primary" onClick={javiKvar}>
              Javi sefu
            </button>
            <button type="button" className="link" onClick={() => setKvarOtvoren(false)}>
              Nazad
            </button>
          </div>
        </div>
      )}

      {tour.actualStart && !tour.actualEnd && !zatvaramTuru && (
        <button
          type="button"
          className="primary driver__finish"
          onClick={() => setZatvaramTuru(true)}
        >
          {remaining === 0
            ? `Završi turu — ${tour.end.name}`
            : `Završi turu — ${remaining} ${remaining === 1 ? 'stanica' : 'stanica'} bez odgovora`}
        </button>
      )}

      {/* PRE ZATVARANJA SE PROLAZI ONO ŠTO NIJE ODRAĐENO.
          Do 15.09.2026. se „Završi turu" nije ni pojavljivalo dok sve stanice
          nisu zatvorene — pa je vozač sa 6/9 prosto zatvorio aplikaciju, a
          operater turu zatvarao sutradan i pogađao šta je bilo.

          On je jedini koji zna da li je bio tamo i da li je robu ostavio. To
          pitanje mora da mu stigne dok je još u vozilu. */}
      {tour.actualStart && !tour.actualEnd && zatvaramTuru && (
        <section className="driver__zatvaranje">
          {neresene.length > 0 ? (
            <>
              <p className="driver__zatvaranje-uvod">
                Pre nego što zatvoriš turu — reci šta je bilo sa{' '}
                <strong>
                  {neresene.length}{' '}
                  {neresene.length === 1 ? 'stanicom' : 'stanica'}
                </strong>
                . Ti si bio tamo, niko drugi ne zna.
              </p>

              {neresene.map((stop) => (
                <div key={stop.id} className="driver__zatvaranje-red">
                  <p className="driver__zatvaranje-ime">
                    {stop.isPickup && <span className="dstop__tag">UZIMA SE</span>}
                    {stop.name}
                    {stop.city ? `, ${stop.city}` : ''}
                  </p>
                  <p className="driver__zatvaranje-roba">
                    {stop.items.map((i) => `${i.articleName} × ${i.quantity}`).join(', ')}
                  </p>

                  <button
                    type="button"
                    className="primary"
                    onClick={() => zatvoriNaKraju(stop, PREDATO_NA_KRAJU, true)}
                  >
                    {stop.isPickup ? 'Bio sam, sve utovario' : 'Bio sam, sve predao'}
                  </button>

                  {(stop.isPickup ? PICKUP_EXCEPTIONS : EXCEPTIONS).map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => zatvoriNaKraju(stop, reason, false)}
                    >
                      {reason}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => zatvoriNaKraju(stop, NISAM_STIGAO, false)}
                  >
                    {NISAM_STIGAO}
                  </button>
                </div>
              ))}
            </>
          ) : (
            <>
              {/* ŠTA SE NOSI NAZAD. Ne blokira zatvaranje — kaže šta se nosi,
                  da vozač to preda u magacinu umesto da prenoći u kombiju. */}
              {ostatak.komada > 0 && (
                <div className="driver__ostatak">
                  <p className="driver__ostatak-naslov">
                    Nosiš nazad <strong>{ostatak.komada}</strong>{' '}
                    {ostatak.komada === 1 ? 'stavku' : 'stavki'} sa{' '}
                    {ostatak.stanica}{' '}
                    {ostatak.stanica === 1 ? 'stanice' : 'stanica'}.
                  </p>
                  <ul>
                    {ostatak.spisak.map((red) => (
                      <li key={red}>{red}</li>
                    ))}
                  </ul>
                  <p className="driver__ostatak-sta">
                    Predaj to u magacinu. Šef magacina ujutru odlučuje šta dalje.
                  </p>
                </div>
              )}

              <button type="button" className="primary driver__finish" onClick={finishTour}>
                {ostatak.komada > 0
                  ? `Završi turu i vrati ${ostatak.komada} u magacin`
                  : `Završi turu — ${tour.end.name}`}
              </button>
            </>
          )}

          <button
            type="button"
            className="link driver__zatvaranje-nazad"
            onClick={() => setZatvaramTuru(false)}
          >
            Nazad na stanice
          </button>
        </section>
      )}

      {tour.actualEnd && (
        <div className="driver__done">
          <p>Tura završena.</p>
          {pending.length === 0 && (
            <button
              type="button"
              onClick={() => {
                storage.clearTour();
                setTour(null);
                // Vozac koji je zavrsio prvu turu skoro uvek trazi drugu. Dok
                // ovoga nije bilo, morao je da skenira QR ponovo — i dobijao
                // upravo zavrsenu turu.
                if (online) void loadTour(true);
              }}
            >
              Završeno — uzmi sledeću turu
            </button>
          )}
        </div>
      )}

      {signingStopId !== null && (
        <SignaturePad
          onDone={(url, ocena) => {
            const stop = stops.find((s) => s.id === signingStopId);
            if (stop) sign(stop, url, ocena);
          }}
          // Zvezdice samo kod kupca i samo pri PRVOM potpisu stanice: ponovni
          // potpis bi bio drugi način da se ocena „popravi".
          ponudiOcenu={(() => {
            const s = stops.find((x) => x.id === signingStopId);
            return !!s && s.kind === 'CustomerAddress' && !s.isPickup && !s.hasSignature;
          })()}
          onCancel={() => { setSigningStopId(null); setPotpisGreska(null); }}
          message={potpisGreska}
        />
      )}
    </div>
  );
}
